import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { SpreadsheetNotesService } from 'api';
import { SpreadsheetNoteGet } from 'api/models/SpreadsheetNoteGet';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import { GET_SPREADSHEET_NOTES_KEY, SPREADSHEET_NOTES_GETTING_NOTES_ERROR_MESSAGE } from 'common/constants/services/spreadsheetNotes/spreadsheetNotes';
import { UseGetNotesParams } from 'services/hooks/notes/useGetNotes';
import { throwError } from 'utilities';
import { SpreadsheetNotesResponse, UseGetSpreadsheetNotesHook } from './types';

const getSpreadsheetNotes = async (params: UseGetNotesParams): Promise<SpreadsheetNotesResponse | null> => {
  const { pageType, pageTypeKey, pageTypeId } = params;

  if (!pageType || !pageTypeKey || isNil(pageTypeId)) {
    return null;
  }

  try {
    const idStr = String(pageTypeId);

    const notes = await SpreadsheetNotesService.apiSpreadsheetNotesList(
      pageType as SpreadsheetNoteGet.page_type,
      pageTypeKey === 'cap_table' ? idStr : undefined,
      pageTypeKey === 'fund_ownership' ? idStr : undefined,
      pageTypeKey === 'financial_statement' ? idStr : undefined,
      pageTypeKey === 'fund_measurement_date' ? idStr : undefined,
      pageTypeKey === 'allocation' ? idStr : undefined,
      pageTypeKey === 'valuation_approach' ? idStr : undefined,
      // valuation_approach__valuation is not part of PageTypeKey union; leave undefined
      undefined,
      pageTypeKey === 'company_measurement_date' ? idStr : undefined,
      pageTypeKey === 'market_adjustment' ? idStr : undefined,
      pageTypeKey === 'fund_investment_ownership' ? idStr : undefined,
      pageTypeKey === 'range_sensitivity' ? idStr : undefined,
    );

    if (!notes) {
      return throwError(SPREADSHEET_NOTES_GETTING_NOTES_ERROR_MESSAGE);
    }

    const sortedNotes: SpreadsheetNotesResponse = notes
      .map(note => ({
        ...note,
        id: note.id ?? undefined,
      }))
      .toSorted((a, b) => new Date(a?.created_at ?? '').getTime() - new Date(b?.created_at ?? '').getTime());

    return sortedNotes;
  } catch (error) {
    return throwError(SPREADSHEET_NOTES_GETTING_NOTES_ERROR_MESSAGE, error);
  }
};

export const useGetSpreadsheetNotes: UseGetSpreadsheetNotesHook = params => {
  const { pageType, pageTypeKey, pageTypeId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<SpreadsheetNotesResponse | null, Error>({
    enabled: Boolean(pageType) && Boolean(pageTypeKey) && !isNil(pageTypeId) && shouldQueryAutomatically,
    queryFn: () => getSpreadsheetNotes({ pageType, pageTypeKey, pageTypeId }),
    queryKey: [GET_SPREADSHEET_NOTES_KEY, pageType, pageTypeKey, pageTypeId],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
  });

  return { data, error, isError, isFetching, isLoading, refetch };
};
